import { awsConfig } from 'core/config';
import { isSafeInternalPath } from './ssoRedirect';

// Handoff for the SSO deep-link: the provider is stashed before the browser
// leaves for the logout chain, then read back when it returns to /login.
const PENDING_SSO_KEY = 'pendingSso';

export type PendingSso = {
  provider: string;
  next?: string;
};

export const savePendingSso = ({ provider, next }: PendingSso) =>
  sessionStorage.setItem(PENDING_SSO_KEY, JSON.stringify({ provider, next }));

export const takePendingSso = (): PendingSso | null => {
  const stored = sessionStorage.getItem(PENDING_SSO_KEY);
  if (!stored) return null;
  sessionStorage.removeItem(PENDING_SSO_KEY);

  try {
    const { provider, next } = JSON.parse(stored) as PendingSso;
    if (!provider) return null;
    return { provider, next: isSafeInternalPath(next) ? next : undefined };
  } catch {
    return null;
  }
};

// Cognito /logout clears the hosted-UI cookie, which otherwise answers the next
// /authorize from cache and never federates; chaining on to Auth0 /v2/logout
// clears the SSO cookie that would then silently re-admit the same identity.
// Auth0's session is tenant-wide, so switching between two providers on one
// tenant needs both. Returns null for a provider with no Auth0 app mapped,
// leaving the caller to federate directly.
export const buildSsoLogoutUrl = (provider: string): string | null => {
  const { oauth, auth0Logout, aws_user_pools_web_client_id } = awsConfig;
  const auth0 = auth0Logout?.[provider];
  if (!auth0?.domain || !auth0.clientId || !oauth?.domain) return null;

  // Both allowlists match the exact string, so returnTo stays unencoded and
  // points at the origin root, and client_id comes first, as registered.
  const returnTo = `${window.location.origin}/`;
  const auth0LogoutUrl = `https://${auth0.domain}/v2/logout?client_id=${auth0.clientId}&returnTo=${returnTo}`;

  return `https://${
    oauth.domain
  }/logout?client_id=${aws_user_pools_web_client_id}&logout_uri=${encodeURIComponent(
    auth0LogoutUrl
  )}`;
};
